/**
 * Resolve the current route to its approved screen contract.
 *
 * Story #5343 (Launch W1). This is the single lookup the four shared-chrome
 * components (ScreenBreadcrumb, DomainTabs, FocusBar, ScreenFooter) depend on.
 * Because it derives everything from the URL, a screen gets its full chrome
 * without its page component being edited at all -- which is the entire point:
 * the 2026-09-02 design-match audit found these four components missing 317
 * times across 143 screens, and 143 separate edits is not a fix, it is the
 * same mistake at scale.
 *
 * Matching is deliberately conservative. A route with no contract row returns
 * null and every chrome component renders nothing, so an unmapped or legacy
 * route degrades to exactly today's appearance rather than to a wrong or
 * invented breadcrumb.
 */
import { useLocation, matchPath } from 'react-router-dom';
import { SCREEN_CONTRACT, type ScreenContract } from './screenContract.generated';
import { WITHDRAWN_SCREEN_IDS } from './scopeDecisions';

/**
 * Routes where the running app deliberately or historically differs from the
 * workbook's canonical route. Keyed by the LIVE path pattern, valued by the
 * screen id it should resolve to.
 *
 * Each entry is a real divergence found by the 2026-09-02 design-match audit.
 * An alias is a stopgap that lets the chrome appear now; the underlying route
 * should be moved to its canonical path, at which point the entry is deleted.
 */
export const ROUTE_ALIASES: ReadonlyArray<{ path: string; id: string; why: string }> = [
  // O23 was here until 2026-09-04, mounted at /logs. Deleted per this block's
  // own rule: the route now IS /operations/logs and /logs redirects to it, so
  // the alias has nothing left to paper over. Keeping it was actively harmful
  // -- the alias satisfied the chrome, which hid the fact that the Logs tab
  // DomainTabs renders on every Operations screen pointed at a route that had
  // never been mounted, so every click landed on NotFound.
  // The shell redirects '/' to Home; H01 is Home's default view.
  { path: '/', id: 'H01', why: "'/' redirects to Home, whose default view is Attention" },
  // C08's contract route is the organisation-scoped deep link
  // /customers/organisations/:organisationId/access, but the Customers tab
  // strip's canonical entry -- and the route the screen is primarily mounted
  // at -- is the unscoped /customers/access-projection. Both reach the same
  // component; only the deep link matches the contract pattern on its own.
  {
    path: '/customers/access-projection',
    id: 'C08',
    why: 'primary mount is unscoped; the contract route is the org-scoped deep link',
  },
  // M06's contract route is /my-account/access; it is mounted at
  // /my-account/effective-access. Same screen, longer word.
  { path: '/my-account/effective-access', id: 'M06', why: 'mounted under a longer name' },
  // E06 is the last survivor of the /e0N numbering. App.tsx explains the
  // rename to /services/:serviceId was skipped because a service is
  // identified by a (serviceType, name) pair, not one id.
  {
    path: '/environments/:environmentId/e06',
    id: 'E06',
    why: 'canonical /services/:serviceId rename deferred - service identity is a pair, not an id',
  },
];

/**
 * Contract screens whose app route diverges in a way an alias must NOT paper
 * over, because aliasing would make the chrome assert a scope the URL does not
 * carry.
 *
 * P07-P11 (Configuration) were here too and are now FIXED: they are mounted at
 * their canonical /portals/:portalId/configuration/... routes, the flat
 * /configuration/* paths redirect there, and the screens read the portal from
 * the route. They get their chrome like every other screen.
 *
 * P23-P27 (Usage & cost) remain, and cannot be fixed the same way. Nesting the
 * route is trivial; making it TRUE is not. The cost API takes a period and an
 * environment and nothing else -- AWS Cost Explorer filters by linked account,
 * so there is no portal dimension in the data at all (see costsApi in
 * services/api.ts). A /portals/{id}/usage-cost/summary route would print
 * "Focus: exact portal" above estate-wide numbers, which is worse than no
 * chrome: it would make the screen assert a scope its figures do not honour.
 * Giving these five a portal needs a portal-to-cost attribution model behind
 * the costs Lambda -- backend work, not a route change.
 */
export const UNSCOPED_DIVERGENCES: ReadonlyArray<{ mountedAt: string; id: string; blockedBy: string }> = [
  { mountedAt: '/usage-cost/summary', id: 'P23', blockedBy: 'costsApi has no portal dimension' },
  { mountedAt: '/usage-cost/trajectory', id: 'P24', blockedBy: 'costsApi has no portal dimension' },
  { mountedAt: '/usage-cost/allocation', id: 'P25', blockedBy: 'costsApi has no portal dimension' },
  { mountedAt: '/usage-cost/ai-roles', id: 'P26', blockedBy: 'costsApi has no portal dimension' },
  { mountedAt: '/usage-cost/investigations', id: 'P27', blockedBy: 'costsApi has no portal dimension' },
];

/** Screen types that are drawers or shell furniture, never a routable page. */
const NON_ROUTABLE_TYPES = new Set(['drawer', 'shell-control']);

const WITHDRAWN = new Set(WITHDRAWN_SCREEN_IDS);

/**
 * The contract rows that can be matched against a URL path.
 *
 * Screens withdrawn from the launch (scopeDecisions.tsx) are excluded, which
 * matters most for the tab strip: DomainTabs lists a screen's siblings, so a
 * withdrawn screen left in would render a tab pointing at a route that no
 * longer exists and 404 anyone who clicked it.
 */
export const ROUTABLE_SCREENS: readonly ScreenContract[] = SCREEN_CONTRACT.filter(
  (s) => s.routePattern && !NON_ROUTABLE_TYPES.has(s.screenType) && !WITHDRAWN.has(s.id),
);

/**
 * More path segments and fewer parameters means a more specific pattern, so
 * /environments/:id/overview beats /environments/:id. Screens carrying a
 * routeQuery outrank those that do not, because they only reach this point
 * having already matched that query.
 */
function specificity(screen: ScreenContract): number {
  const segments = screen.routePattern.split('/').filter(Boolean);
  const params = segments.filter((s) => s.startsWith(':')).length;
  return segments.length * 10 - params + (screen.routeQuery ? 5 : 0);
}

/**
 * Find the contract row for a path plus query string.
 *
 * Exported separately from the hook so it can be unit-tested over every route
 * in the app without mounting a router.
 */
export function resolveScreenContract(
  pathname: string,
  search = '',
): ScreenContract | null {
  const params = new URLSearchParams(search);

  const aliased = ROUTE_ALIASES.find((a) => matchPath({ path: a.path, end: true }, pathname));
  if (aliased) return SCREEN_CONTRACT.find((s) => s.id === aliased.id) ?? null;

  const onPath = ROUTABLE_SCREENS.filter((screen) =>
    matchPath({ path: screen.routePattern, end: true }, pathname),
  );
  if (onPath.length === 0) return null;

  const matched = onPath.filter((screen) => {
    if (!screen.routeQuery) return true;
    const [key, value] = screen.routeQuery.split('=');
    return params.get(key) === value;
  });

  if (matched.length > 0) {
    return [...matched].sort((a, b) => specificity(b) - specificity(a))[0];
  }

  // The path matched only screens discriminated by a query the URL does not
  // carry -- /home with no ?view. The design opens such a path on its first
  // tab, so resolve to the first in workbook order rather than to nothing.
  return onPath[0];
}

/** Same resolution, bound to the router's current location. */
export function useScreenContract(): ScreenContract | null {
  const { pathname, search } = useLocation();
  return resolveScreenContract(pathname, search);
}

/**
 * Every screen sharing a contract row's domain AND group, in workbook order.
 * This is the per-domain tab strip's item list -- e.g. A02 returns all six
 * "Administration -> Identity" screens (Users, Invitations, Access, Reviews,
 * Service identities, Provider/pool advanced).
 */
export function siblingScreens(screen: ScreenContract): readonly ScreenContract[] {
  return ROUTABLE_SCREENS.filter(
    (s) => s.lhs === screen.lhs && s.group === screen.group,
  );
}
